import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import { PaginationQueryParams } from 'types/queryTypes';
import { SortOrder } from 'hooks/useTableControls';
import {
  CreateDataSourcePayload,
  DataSourceField,
  DataSourceFieldsPatchPayload,
  DataSourcesDto,
  DataSourcesListView,
  DataSourceTemplate,
} from 'types/dataSourcesTypes';
import { useUI } from 'contexts/UiContext';

const DATA_SOURCE_QUERY_KEY = 'data-sources';
const DATA_SOURCE_BY_ID_QUERY = 'data-source-by-id';
const DATA_SOURCE_FIELDS_QUERY_KEY = 'data-source-fields';
const DATA_SOURCE_TEMPLATES_QUERY_KEY = 'data-source-templates';
const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;

/**
 * Get all data sources by company
 */
export const useGetAllDataSourcesByCompany = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'name', order: 'ASC' },
}: PaginationQueryParams & { companyId: string } & {
  sortOrder: SortOrder;
}) =>
  useQuery({
    queryKey: [DATA_SOURCE_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<DataSourcesListView[]>(
        `${REACT_APP_DARWIN_URL}/data-sources/company/${companyId}?limit=${limit}&page=${page}&search=${query}&order=${sortOrder.order}&sortBy=${sortOrder.sortBy}`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

/**
 * Post create data source
 */
export const useCreateDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
    }: {
      payload: CreateDataSourcePayload;
      companyId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/data-sources/company/${companyId}`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_SOURCE_QUERY_KEY] });
    },
  });
};

/**
 * Get data source by id
 */
export const useGetDataSourceById = (
  companyId: string,
  dataSourceId: string
) => {
  return useQuery({
    queryKey: [DATA_SOURCE_BY_ID_QUERY, companyId, dataSourceId],
    queryFn: () =>
      fetchFromApi<DataSourcesDto>(
        `${REACT_APP_DARWIN_URL}/data-sources/${companyId}/${dataSourceId}`
      ),
    enabled: !!companyId && !!dataSourceId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Update a data source
 */
export const useUpdateDataSourceByID = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      payload,
      companyId,
      dataSourceId,
    }: {
      payload: CreateDataSourcePayload;
      companyId: string | number;
      dataSourceId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/data-sources/${dataSourceId}/company/${companyId}/`,
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_SOURCE_QUERY_KEY] });
    },
  });
};

/**
 * Delete data source service
 */
export const useDeleteDataSource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      dataSourceId,
    }: {
      companyId: string | number;
      dataSourceId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/data-sources/${dataSourceId}/company/${companyId}/`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATA_SOURCE_QUERY_KEY] });
    },
  });
};

/**
 * Fetch data source fields.
 */
export const useGetDataSourceFields = ({
  dataSourceId = '',
  templateId = '',
}: {
  dataSourceId?: string;
  templateId?: string;
}) => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [
      DATA_SOURCE_FIELDS_QUERY_KEY,
      selectedCompany,
      dataSourceId,
      templateId,
    ],
    queryFn: () =>
      fetchFromApi<DataSourceField[]>(
        `${REACT_APP_DARWIN_URL}/data-sources/${dataSourceId}/fields/company/${selectedCompany}${
          templateId ? `?templateId=${templateId}` : ''
        }`
      ),
  });
};

/**
 * Fetch data source templates.
 */
export const useGetDataSourceTemplates = () => {
  const { selectedCompany } = useUI();

  return useQuery({
    queryKey: [DATA_SOURCE_TEMPLATES_QUERY_KEY, selectedCompany],
    queryFn: () =>
      fetchFromApi<DataSourceTemplate[]>(
        `${REACT_APP_DARWIN_URL}/data-sources/templates/company/${selectedCompany}`
      ),
  });
};

/**
 * Bulk update data source fields.
 */
export const usePatchDataSourceFields = () => {
  const { selectedCompany } = useUI();

  return (dataSourceId: string, payload: DataSourceFieldsPatchPayload) => {
    return fetchFromApi(
      `${REACT_APP_DARWIN_URL}/data-sources/${dataSourceId}/fields/company/${selectedCompany}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }
    );
  };
};
